import { storageDocuments } from "@/firebase/firebaseConfig";
import { createHash } from "crypto";
import moment from "moment";
import enterpriseFormService from "@/services/enterprises/enterpriseFormService";
import isNull from "lodash/isNull";
import isString from "lodash/isString";
import isObject from "lodash/isObject";
import isNil from "lodash/isNil";
import { saveAs } from "file-saver";
import { ColorMixin } from "@/mixins";
const { AISEA_COLOR } = ColorMixin.data();

export const operationLoadingStart = (
  vc //  VueComponent
) => {
  vc.$vs.loading({ color: AISEA_COLOR.BLUE });
};

export const operationLoadingClose = (
  vc //  VueComponent
) => {
  vc.$vs.loading.close();
};

// システムエラー通知
export const notifySystemError = (
  vc, //  VueComponent
  options = {
    fixed: false,
    position: "bottom-right",
  }
) => {
  const i18n = vc.$t("Views.Operation.Error.SystemError");
  vc.$vs.notify({
    title: i18n.title,
    text: i18n.text,
    color: "danger",
    iconPack: "feather",
    icon: "icon-x-square",
    ...options,
  });
};

// 404エラー通知
export const notifyNotFoundError = (
  vc, //  VueComponent
  options = {
    fixed: false,
    position: "bottom-right",
  }
) => {
  const i18n = vc.$t("Views.Operation.Error.NotFoundError");
  vc.$vs.notify({
    title: i18n.title,
    color: "danger",
    ...options,
  });
};

// レポート削除成功通知
export const notifyDeleteSuccess = (
  vc, //  VueComponent
  options = {
    fixed: false,
    position: "top-center",
  }
) => {
  const i18n = vc.$t("Views.Operation.Delete.Success");
  vc.$vs.notify({
    text: i18n.text,
    color: "success",
    ...options,
  });
};

// レポート削除失敗通知
export const notifyDeleteFailed = (
  vc, //  VueComponent
  options = {
    fixed: false,
    position: "top-center",
  }
) => {
  const i18n = vc.$t("Views.Operation.Delete.Fail");
  vc.$vs.notify({
    text: i18n.text,
    color: "danger",
    ...options,
  });
};

// レポート画像ダウンロード失敗通知
export const notifyDownloadImageFailed = (
  vc, //  VueComponent
  options = {}
) => {
  const i18n = vc.$t("Views.Operation.ImageManipulation.Download.Fail");
  vc.$vs.notify({
    text: i18n.text,
    color: "danger",
    fixed: false,
    position: "bottom-right",
    ...options,
  });
};

// トランザクション成功通知(デフォルト: 差し戻し)
export const notifyTransactionSuccess = (
  vc, //  VueComponent
  transactionLabel, // string
  options = {}
) => {
  const isLabelValid =
    isString(transactionLabel) && transactionLabel.length > 0;
  if (!isLabelValid) {
    throw new Error("Invalid transaction label");
  }
  const text = vc.$t(`Views.Operation.Transaction.Success.text`, {
    transactionLabel,
  });
  vc.$vs.notify({
    text,
    color: "success",
    fixed: false,
    position: "top-center",
    ...options,
  });
};

// トランザクション失敗通知
export const notifyTransactionFailed = (
  vc, //  VueComponent
  transactionLabel, // string
  options = {}
) => {
  const isLabelValid =
    isString(transactionLabel) && transactionLabel.length > 0;
  if (!isLabelValid) {
    throw new Error("Invalid transaction label");
  }
  const text = vc.$t(`Views.Operation.Transaction.Fail.text`, {
    transactionLabel,
  });
  vc.$vs.notify({
    text,
    color: "danger",
    fixed: false,
    position: "top-center",
    ...options,
  });
};
// データ更新成功通知
export const notifyUpdateSuccess = (
  vc, //  VueComponent
  options = {}
) => {
  const text = vc.$t(`Views.Operation.Edit.Success.text`);
  vc.$vs.notify({
    text,
    color: "success",
    fixed: false,
    position: "top-center",
    ...options,
  });
};

// データ更新失敗通知
export const notifyUpdateFailed = (
  vc, //  VueComponent
  options = {}
) => {
  const text = vc.$t(`Views.Operation.Edit.Fail.text`);
  vc.$vs.notify({
    text,
    color: "danger",
    fixed: false,
    position: "top-center",
    ...options,
  });
};

// 一時ファイル作成成功通知
export const notifyUploadTempImageSuccess = (
  vc, //  VueComponent
  options = {}
) => {
  vc.$vs.notify({
    text: "ファイルの追加に成功しました",
    color: "success",
    fixed: false,
    position: "top-center",
    ...options,
  });
};

// 一時ファイル作成失敗通知
export const notifyUploadTempImageFailed = (
  vc, //  VueComponent
  options = {}
) => {
  // TODO: i18
  vc.$vs.notify({
    text: "ファイルの追加に失敗しました",
    color: "danger",
    fixed: false,
    position: "top-center",
    ...options,
  });
};

// レポート一覧画面に遷移
export const pushReportList = (
  vc //  VueComponent
) => {
  vc.$router.push({
    name: "addon-operation-reports-index",
  });
};

// 詳細・編集に表示する更新日時フォーマットに変換
export const formatDateTime = (
  dateString // like 2024-03-15T09:06:08.000Z
) => {
  return moment(dateString).format("YYYY/MM/DD HH:mm"); // string
};

// レポートの値がstringでformatがある場合にフォーマットを適用する
// NOTE: 現在は日時に関わるデータの場合しか想定していない
export const applyFormat = (reportValue, enterpriseForm) => {
  if (!enterpriseFormService.hasFormat(enterpriseForm)) {
    return reportValue;
  }
  if (!isString(reportValue)) return reportValue; // any

  const isTime = enterpriseFormService.isTimePickers(enterpriseForm);
  const dateValue = isTime
    ? moment(reportValue, "HH:mm:ss") // Time
    : moment(reportValue); // Date / DateTime
  return dateValue.format(enterpriseForm.format); // string
};

// value_json が文字列で来た場合に備えてパースする（object/array はそのまま返す）
const parseMaybeJson = value => {
  if (isString(value)) {
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  }
  return value;
};

// FLEXIBLE_TIME_PICKER の選択肢ラベル（詳細時刻 "HH:MM" はそのまま表示）
const FLEXIBLE_TIME_LABELS = { AM: "午前", PM: "午後", UNDECIDED: "未定" };
const formatFlexibleTime = value => {
  if (!isString(value) || value === "") return value;
  return FLEXIBLE_TIME_LABELS[value] || value;
};

/**
 * レポート値を read 表示用の文字列に整形する。
 * 既存 type は applyFormat 同等。新 type（value_json 系 / FLEXIBLE_TIME_PICKER）は
 * 人間可読な文字列へ整形する。未対応・未知 type が来ても throw せず安全に表示する。
 * @param {Object} enterpriseFormVersionItem
 * @param {*} rawValue - getValueFromReportFormVersionItem の戻り値
 * @returns {string|number|*}
 */
// 荷役種別（binding_key=CARGO_TYPE）の固定ラベル
const CARGO_TYPE_LABEL_BY_VALUE = { LOADING: "積", UNLOADING: "揚" };

export const formatReportValueForDisplay = (
  enterpriseFormVersionItem,
  rawValue,
  masterMaps = {} // { portMap, districtMap, berthMap, cargoMap } 港/貨物の ID→名称解決用（任意）
) => {
  const efs = enterpriseFormService;
  const item = enterpriseFormVersionItem;
  if (isNil(rawValue)) return rawValue;

  // 荷役種別（CARGO_TYPE）は input_type に依らず binding_key で固定ラベルに変換する
  if (item && item.binding_key === "CARGO_TYPE") {
    return CARGO_TYPE_LABEL_BY_VALUE[rawValue] != null
      ? CARGO_TYPE_LABEL_BY_VALUE[rawValue]
      : rawValue;
  }

  // 14: DATE_RANGE_PICKER → "start 〜 end"
  if (efs.isDateRangePicker(item)) {
    const v = parseMaybeJson(rawValue);
    if (!isObject(v)) return "";
    const start = v.start || "";
    const end = v.end || "";
    if (!start && !end) return "";
    return `${start} 〜 ${end}`;
  }
  // 15: DATE_MULTI_SELECT_FROM_RANGE → "d1, d2, ..."
  if (efs.isDateMultiSelect(item)) {
    const v = parseMaybeJson(rawValue);
    return Array.isArray(v) ? v.join(", ") : "";
  }
  // 16: FLEXIBLE_TIME_PICKER → 午前/午後/未定/HH:MM
  if (efs.isFlexibleTime(item)) {
    return formatFlexibleTime(rawValue);
  }
  // 17: PORT_HIERARCHY_PICKER → 港名 / 地区名 / バース名（マスタ未取得時は ID にフォールバック）
  if (efs.isPortHierarchyPicker(item)) {
    const v = parseMaybeJson(rawValue);
    if (!isObject(v)) return "";
    const { portMap = {}, districtMap = {}, berthMap = {} } = masterMaps || {};
    const nameOrId = (map, id) => {
      if (id == null || id === "") return null;
      const entry = map[String(id)];
      return entry && entry.name != null ? entry.name : id;
    };
    return [
      nameOrId(portMap, v.port_id),
      nameOrId(districtMap, v.port_district_id),
      nameOrId(berthMap, v.port_berth_id),
    ]
      .filter(x => x != null && x !== "")
      .join(" / ");
  }
  // 18: CARGO_PICKER → 貨物名 / 数量 単位（マスタ未取得時は ID にフォールバック）
  if (efs.isCargoPicker(item)) {
    const v = parseMaybeJson(rawValue);
    if (!isObject(v)) return "";
    const { cargoMap = {} } = masterMaps || {};
    const cargo =
      v.cargo_type_id != null ? cargoMap[String(v.cargo_type_id)] : null;
    const cargoName =
      cargo && cargo.name != null ? cargo.name : v.cargo_type_id;
    const parts = [];
    if (cargoName != null && cargoName !== "") parts.push(cargoName);
    if (v.quantity != null && v.quantity !== "") {
      const qtyNum = Number(v.quantity);
      const qty = Number.isFinite(qtyNum)
        ? qtyNum.toLocaleString("ja-JP")
        : v.quantity;
      parts.push(v.unit ? `${qty} ${v.unit}` : `${qty}`);
    }
    return parts.join(" / ");
  }
  // value_files（ファイル）: ファイル名で表示する（テーブルセル等の文字列表示用。
  // 通常フォームの画像ファイルは FormItemFile が担当するためここには来ない）。
  // DATE_MULTI_SELECT_FROM_RANGE(15) の配列は上で処理済みのため、ここに来る配列はファイル想定。
  if (Array.isArray(rawValue)) {
    return rawValue
      .map(f => (f && typeof f === "object" ? getDisplayFileName(f.name) : f))
      .filter(v => v != null && v !== "")
      .join(", ");
  }
  // 既存 type は従来通り applyFormat
  return applyFormat(rawValue, item);
};

// レポートの値に単位を適用する
export const applyUnit = (
  reportValue, // string | number | null;
  enterpriseForm // EnterpriseForm;
) => {
  if (isNull(reportValue)) return reportValue;
  if (!enterpriseFormService.hasUnit(enterpriseForm)) {
    return reportValue;
  }
  return `${reportValue} ${enterpriseForm.unit}`.trim();
};

// storageのパスを生成
export const createStorageFilePath = (
  enterpriseId, // string
  reportId, // string
  formVersionItemId, // string => マスタの方
  fileName, // string
  repeatIndex = null // number | null
) => {
  const rowDir = repeatIndex == null ? "" : `rows:${repeatIndex}/`;
  return `e:${enterpriseId}/operation/reports:${reportId}/items:${formVersionItemId}/${rowDir}${fileName}`; // string
};

/**
 * API の name が "ハッシュ/ファイル名" 形式の場合にファイル名のみ返す。保存後はファイル名のみのためそのまま返す。
 * @param {string} name - value_files[].name（例: "eb37.../テスト２.xlsx" または "テスト２.xlsx"）
 * @returns {string} 表示用ファイル名
 */
export const getDisplayFileName = name => {
  if (name == null || typeof name !== "string") return "";
  const lastSlash = name.lastIndexOf("/");
  return lastSlash >= 0 ? name.slice(lastSlash + 1) : name;
};

/** 画像としてプレビュー表示する拡張子（小文字） */
const IMAGE_EXTENSIONS = ["jpg", "jpeg", "png", "gif", "webp", "heic"];

/**
 * ファイル名の拡張子から画像かどうかを判定する。SINGLE_IMAGE_FILE でプレビュー表示するかファイル名表示にするかの切り替えに使用。
 * @param {string} name - ファイル名（パス含む可）
 * @returns {boolean} 画像拡張子なら true
 */
export const isImageFileByName = name => {
  if (name == null || typeof name !== "string") return false;
  const base = getDisplayFileName(name);
  const lastDot = base.lastIndexOf(".");
  const ext = lastDot >= 0 ? base.slice(lastDot + 1).toLowerCase() : "";
  return IMAGE_EXTENSIONS.includes(ext);
};

// 画像を一意に特定するためにhashを作成するのに使用
export const createOperationHash = (
  message = "" // : string
) => {
  const hash = createHash("sha256");
  hash.update(message);
  return hash.digest("hex"); // string
};

const isDownloadImageValid = (
  image // {url: string; name: string;}
) => {
  if (!isObject(image)) return false;
  const { url, name } = image;
  return isString(url) && url !== "" && isString(name) && name !== "";
};

/**
 * Downloads an image from the specified URL and saves it with the given name.
 * @param {Object} options - The options for downloading the image.
 * @param {string} options.url - The URL of the image to download.
 * @param {string} options.name - The name to save the downloaded image as.
 * @returns {Promise<void>} - A promise that resolves when the image is downloaded and saved.
 * @throws {Error} If the image data is invalid.
 */
export const downloadImage = async ({
  url, // : string
  name, // string
}) => {
  if (!isDownloadImageValid({ url, name })) {
    throw new Error("Invalid image data.");
  }
  saveAs(url, name);
};

// 画像のURLからblobを作成する
export const getBlogUrl = async (
  url // : string
) => {
  const response = await fetch(url);
  const blob = await response.blob();
  return blob;
};

// 画像のblobからdataURLを作成する -> 何度も画像のURLにアクセスしないようにするため
export const readFileAsDataURL = async blob => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      resolve(reader.result);
    };
    reader.onerror = () => {
      reject();
    };
    reader.readAsDataURL(blob);
  });
};

// 画像のURLからblobを作成してdataURLに変換する
export const getDataURLFromStoragePath = async (
  path // string
) => {
  const imageUrl = await storageDocuments.child(path).getDownloadURL();
  const blob = await getBlogUrl(imageUrl);
  const dataURL = await readFileAsDataURL(blob);
  return dataURL;
};

// ストレージパスから Firebase の HTTPS ダウンロードURLを取得する（別タブ表示・href 用）。
// dataURL（data:）は別タブ遷移がブラウザにブロックされるため、こちらの https URL を使う。
export const getDownloadUrlFromStoragePath = async (
  path // string
) => {
  return storageDocuments.child(path).getDownloadURL(); // string（https://firebasestorage...）
};

// routeが詳細画面か判定
export const isShowScreen = (
  route // Vue-Router's Route object
) => {
  if (!isObject(route)) return false;
  return route.name === "addon-operation-reports-show";
};

// routeが編集画面か判定
export const isEditScreen = (
  route // Vue-Router's Route object
) => {
  if (!isObject(route)) return false;
  return route.name === "addon-operation-reports-edit";
};

// 編集画面に遷移
export const pushEditScreen = (
  vc, // VueComponent { $router: VueRouter; }
  { aoEnterpriseId, reportId } // router parameter object { aoEnterpriseId: `${number}`; reportId: `${number}`; }
) => {
  if (!vc.$store.getters["operationReport/isReportUpdatable"]) return false; // 編集権限がない場合は遷移しない
  if (isNaN(aoEnterpriseId) || isNaN(reportId)) return false; // どちらかが数値でない場合は遷移しない
  vc.$router.push({
    name: "addon-operation-reports-edit",
    aoEnterpriseId,
    reportId,
  });
};

// 詳細画面に遷移(同一タブ)
export const pushShowScreen = (
  vc, // VueComponent { $router: VueRouter; }
  { aoEnterpriseId, reportId } // router parameter object { aoEnterpriseId: `${number}`; reportId: `${number}`; }
) => {
  if (isNaN(aoEnterpriseId) || isNaN(reportId)) return false; // どちらかが数値でない場合は遷移しない
  vc.$router.push({
    name: "addon-operation-reports-show",
    aoEnterpriseId,
    reportId,
  });
};

// NOTE: レポート自体の編集権限があるかどうかの判定
// 個別のフォームの編集権限は別途判定する必要がある
export const isEditingReport = (
  vc // VueComponent
) => {
  if (!vc.$store.getters["operationReport/isReportUpdatable"]) return false; // 編集権限がない場合はfalse
  if (!isObject(vc.$route.params)) return false;
  const { aoEnterpriseId, reportId } = vc.$route.params;
  return (
    vc.$route.name === "addon-operation-reports-edit" &&
    !isNaN(aoEnterpriseId) &&
    !isNaN(reportId)
  );
};

// 特殊デフォルト: NOW=現在日時, LATEST=直前レポートの値
const DEFAULT_VALUE_LATEST = "LATEST";

const isDefaultValueLATEST = defaultValue =>
  isString(defaultValue) && defaultValue.toUpperCase() === DEFAULT_VALUE_LATEST;

/**
 * フォーム項目の default_value が 'LATEST' のとき、直前レポートから取得した値を返す。
 * 編集時に「LATEST」を直前の値で置き換える表示・store同期で使用。
 *
 * @param {VueComponent} vc
 * @param {Object} enterpriseFormVersionItem
 * @returns {string|number|string[]|null}
 */
export const getLatestReportValueForFormItem = (
  vc,
  enterpriseFormVersionItem
) => {
  const defaultValue = enterpriseFormService.getDefaultValue(
    enterpriseFormVersionItem
  );
  if (!isDefaultValueLATEST(defaultValue)) return null;
  const map = vc.$store.getters["operationReport/latestReportValueMap"];
  if (!map || !isObject(map)) return null;
  const itemId = enterpriseFormVersionItem.id;
  const previousItem = map[String(itemId)] || map[itemId];
  if (!previousItem) return null;
  try {
    const valueType = enterpriseFormService.getValueType(
      enterpriseFormVersionItem
    );
    if (!valueType) return null;
    const raw = previousItem[valueType];
    if (isNil(raw)) return null;
    if (isString(raw) && raw === "") return null;
    if (Array.isArray(raw) && raw.length === 0) return null;
    return raw;
  } catch {
    return null;
  }
};

// formの情報とレポートの情報からデフォルト値を取得する
// default_value があればそれを返す（NOW/LATEST は特別処理、CAL式は除外）
// default_value_id があればそこから計算した値を返す
// それ以外はnullを返す
export const calcDefaultValue = (
  vc, // VueComponent
  enterpriseFormVersionItem
) => {
  if (enterpriseFormService.hasDefaultValue(enterpriseFormVersionItem)) {
    const defaultValue = enterpriseFormService.getDefaultValue(
      enterpriseFormVersionItem
    );
    if (defaultValue === "NOW") return moment().toISOString();
    if (isDefaultValueLATEST(defaultValue)) {
      const val = getLatestReportValueForFormItem(
        vc,
        enterpriseFormVersionItem
      );
      return val;
    }
    // CAL式は autoComputeCalCells が担うため、リテラルのデフォルト値としては扱わない
    if (parseCalFormula(defaultValue) !== null) return null;
    return defaultValue; // その他の default_value
  }
  const reportFormVersionItemMap =
    vc.$store.getters[
      "operationReport/reportFormVersionItemMapByFormVersionItemId"
    ];
  return enterpriseFormService.getDefaultValueByDefaultValueId(
    enterpriseFormVersionItem,
    reportFormVersionItemMap
  ); // default_value_id から導いた値 | null
};

// 更新時に使用するformの値を作成する
export const calcNextFormItemValue = (
  // vc // VueComponent
  formVersionItem, // {input_type: number; }
  reportFormVersionItem, // {form_version_item_id: `${number}`}
  nextFormValue // { value: number | string | date | [{ "name": string; }], nullableChecked: boolean }
) => {
  // {
  // reportFormVersionId: string;
  //        {
  //           "form_version_item_id": `${number}`,
  //           "nullable_checked": boolean | null,
  //           "value_date": string | null; "2024-04-18"
  //           "value_date_time": string | null; "2024-04-18T05:00:21.564Z"
  //           "value_files": [
  //             {
  //               "name": string | null; // NOTE: 一時ファイルを作成しておく必要がある
  //             }
  //           ],
  //           "value_number": number;
  //           "value_string": string | null;
  //           "value_time": string | null; "05:00:21"
  //         }
  // }
  const nullableChecked = nextFormValue.nullableChecked === true;
  const nextValue = calcNextValueWithInputType(formVersionItem, nextFormValue);
  return {
    ...reportFormVersionItem,
    ...nextValue,
    nullable_checked: nullableChecked,
    form_version_item_id: formVersionItem.id,
  };
};

const calcNextValueWithInputType = (
  formVersionItem, // {input_type: number; }
  nextFormValue // { value: number | string | date | [{ "name": string; }] }
) => {
  const key = enterpriseFormService.getValueType(formVersionItem);
  // 未対応/未知 type（getValueType=null。SPACE/BINDING 等）は値フィールドを持たないため
  // 何も書き込まない（既存値はスプレッドで保持される）。
  if (!key) return {};
  const nextValue = calcNextValue(formVersionItem, nextFormValue);

  return { [key]: nextValue };
  // return { [key]: nextFormValue.value };
  // {"value_date": string | null; "2024-04-18"} | {"value_date_time": string | null; "2024-04-18T05:00:21.564Z"}
  // | {
  //     "value_files": [
  //             {
  //               "name": string | null; // NOTE: 一時ファイルを作成しておく必要がある
  //             }
  //           ],
  //   }
  // | {"value_number": number;}
  // | {"value_string": string | null;}
  // | {"value_time": string | null; "05:00:21"}
};

const calcNextValue = (
  formVersionItem, // {input_type: number; }
  nextFormValue // { value: number | string | date | [{ "name": string; }] }
) => {
  // NOTE:
  // 0: FIXED (固定値): 未実装
  // 1: DATE_PICKER (年月日)
  // 2: TIME_PICKER (時間)
  // 3: SPACE（レイアウト調整用）: 未実装
  // 4: DATETIME_PICKER (日時)
  // 5: SELECT (選択項目)
  // 6: HIERARCHICAL_SELECT (２階層選択): 未実装
  // 7: CHECK_BOX (チェックボックス): 未実装
  // 8: RADIO_BUTTON (ラジオボタン)
  // 9: NUMBER (数値入力)
  // 10: SHORT_TEXT  (短いテキスト入力)
  // 11: LONG_TEXT  (長いテキスト入力)
  // 12: SINGLE_IMAGE_FILE (1ファイル・画像アップロード) 個別対応
  const efs = enterpriseFormService;
  if (efs.isDatePickers(formVersionItem))
    return calcValueDate(nextFormValue.value);
  if (efs.isNumber(formVersionItem)) {
    if (isNil(nextFormValue.value)) return null;
    if (nextFormValue.value === "") return null;
    if (isNaN(nextFormValue.value)) return null;
    return Number(nextFormValue.value);
  }
  return nextFormValue.value;
};

// Dateオブジェクトを value_date に変換
export const calcValueDate = (
  date // Date
) => {
  const momentDate = moment(date);
  if (!momentDate.isValid()) return "";
  const valueDate = momentDate.format("YYYY-MM-DD");
  return valueDate; // string like "2024-04-18"
};

// 編集フォームで使用するコンポーネント名を取得
export const calcEditComponentName = (
  formVersionItem // {input_type: number; }
) => {
  // NOTE:
  // 0: FIXED (固定値): 未実装
  // 1: DATE_PICKER (年月日)
  // 2: TIME_PICKER (時間)
  // 3: SPACE（レイアウト調整用）: 未実装
  // 4: DATETIME_PICKER (日時)
  // 5: SELECT (選択項目)
  // 6: HIERARCHICAL_SELECT (２階層選択): 未実装
  // 7: CHECK_BOX (チェックボックス): 未実装
  // 8: RADIO_BUTTON (ラジオボタン)
  // 9: NUMBER (数値入力)
  // 10: SHORT_TEXT  (短いテキスト入力)
  // 11: LONG_TEXT  (長いテキスト入力)
  // 12: SINGLE_IMAGE_FILE (1ファイル(画像)アップロード) 個別に対応
  const efs = enterpriseFormService;
  if (efs.isDatePickers(formVersionItem)) return "form-item-date-picker";
  if (efs.isTimePickers(formVersionItem)) return "form-item-time-picker";
  if (efs.isDateTimePickers(formVersionItem)) {
    return "form-item-date-time-picker";
  }
  if (efs.isSpace(formVersionItem)) {
    return "form-item-space";
  }
  if (efs.isSelect(formVersionItem)) return "form-item-select";
  if (efs.isRadioButton(formVersionItem)) return "form-item-radio-buttons";
  if (efs.isNumber(formVersionItem)) return "form-item-number";
  if (efs.isShortText(formVersionItem)) return "form-item-short-text";
  if (efs.isLongText(formVersionItem)) return "form-item-long-text";
  // if (efs.isSingleImageFile(formVersionItem)) return "form-item-long-text";
  // 14/15/16（DATE_RANGE_PICKER / DATE_MULTI_SELECT_FROM_RANGE / FLEXIBLE_TIME_PICKER）の編集コンポーネント。
  if (efs.isDateRangePicker(formVersionItem))
    return "form-item-date-range-picker";
  if (efs.isDateMultiSelect(formVersionItem))
    return "form-item-date-multi-select";
  if (efs.isFlexibleTime(formVersionItem)) return "form-item-flexible-time";

  // --- 編集UIを提供しない type は read-only（FormItemUnsupported）で「明示的に」表示する ---
  if (efs.isCheckbox(formVersionItem)) return "form-item-unsupported"; // 7: CHECK_BOX（編集は未実装のため read-only）
  if (efs.isPortHierarchyPicker(formVersionItem))
    return "form-item-unsupported"; // 17: PORT_HIERARCHY_PICKER（仕様で read-only。編集は配船ドロワーの責務）
  if (efs.isCargoPicker(formVersionItem)) return "form-item-unsupported"; // 18: CARGO_PICKER（仕様で read-only）
  if (efs.isBinding(formVersionItem)) return "form-item-unsupported"; // 19: BINDING（レポートでは値を持たない・read-only）

  throw new Error("Invalid form version item."); // 該当しない場合はエラー
};

/**
 * "CAL[#ID1 + #ID2 - #ID3 ...]" 形式の default_value をパースする。
 * @param {string} defaultValue
 * @returns {{ ids: string[], operators: string[] } | null}
 */
export const parseCalFormula = defaultValue => {
  if (!isString(defaultValue)) return null;
  const match = defaultValue.match(/^CAL\[(.+)\]$/i);
  if (!match) return null;
  const inner = match[1].trim();
  const tokens = inner.split(/\s+/);
  const ids = [];
  const operators = [];
  for (const token of tokens) {
    if (token.startsWith("#")) {
      ids.push(token.slice(1));
    } else if (["+", "-", "*", "/"].includes(token)) {
      operators.push(token);
    }
  }
  if (ids.length === 0) return null;
  return { ids, operators };
};

/**
 * parseCalFormula の結果と reportFormVersionItemMap を使って CAL 式を評価する。
 * @param {{ ids: string[], operators: string[] }} formula
 * @param {Object} reportFormVersionItemMap form_version_item_id -> reportFormVersionItem
 * @returns {number | null}
 */
export const evalCalFormula = (formula, reportFormVersionItemMap) => {
  if (!formula || !Array.isArray(formula.ids) || formula.ids.length === 0)
    return null;
  if (!isObject(reportFormVersionItemMap)) return null;

  const rawValues = formula.ids.map(id => {
    const item = reportFormVersionItemMap[String(id)];
    if (!item) return null;
    const val = item.value_number;
    if (val === null || val === undefined) return null;
    return Number(val);
  });

  // 全ての依存値が未入力の場合は計算しない
  if (rawValues.every(v => v === null)) return null;
  // NaN（不正値）が含まれる場合も計算しない
  if (rawValues.some(v => v !== null && isNaN(v))) return null;

  // null は 0 として扱い計算する
  const values = rawValues.map(v => (v === null ? 0 : v));

  let result = values[0];
  for (let i = 0; i < formula.operators.length; i++) {
    const op = formula.operators[i];
    const nextVal = values[i + 1];
    if (op === "+") result += nextVal;
    else if (op === "-") result -= nextVal;
    else if (op === "*") result *= nextVal;
    else if (op === "/") {
      if (nextVal === 0) return null;
      result /= nextVal;
    }
  }

  return result;
};

// レポート更新時に全てのフォームを検証する
// this.$validator.validateAll() では上手く検証できなかったため、formのDOMを取得して検証する
export const validateEditForms = async (
  vc, // VueComponent
  document // Document Object
) => {
  const formElements = document.querySelectorAll(
    "[name^='operation-report-form-enable']"
  ); // NOTE: operation-report-form-enable で始まるname属性を持つ要素(活性状態のフォーム)を取得
  const formNames = Array.from(formElements).map(element =>
    element.getAttribute("name")
  );

  const validatePromises = formNames.map(async formName => {
    const isValid = vc.$validator.validate(formName);
    return isValid;
  });
  try {
    const validateResult = await Promise.all(validatePromises);
    const reducedResult = validateResult.reduce(
      (acc, cur) => acc && cur,
      validatePromises.length > 0 // formがない場合はfalseを返す
    ); // 全ての検証結果がtrueの場合にtrueを返す
    return reducedResult;
  } catch {
    return false;
  }
};
